import { formatCurrency } from '@/wabunka-src/common/utils/currency';
import type { WabunkaReservationForm, WabunkaReservationWithGuestTypeForm } from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaReservations';

import type { WabunkaCurrencyConvertedPrice } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_currency';
import type { WabunkaExperienceFeeChargeType } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_fee';
import type {
  WabunkaExperienceGuestTypeBasedPrice,
  WabunkaExperiencePreReservationGuestTypeBasedPriceCalculationRequestInput,
} from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_guest_type_based_price';
import type {
  WabunkaExperiencePreReservationPrice,
  WabunkaExperiencePreReservationPriceCalculateRequestInput,
} from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_pre_reservation_price';

export interface CurrencyConvertedPriceViewModel {
  amount: number;
  currency: string;
  formattedAmount: string;
}

export const mapCurrencyConvertedPrice = (dp?: WabunkaCurrencyConvertedPrice): CurrencyConvertedPriceViewModel | undefined => {
  if (!dp) return undefined;
  return {
    amount: dp.amount,
    currency: dp.currency,
    formattedAmount: formatCurrency(dp.amount, dp.currency),
  };
};

const buildExperienceEventInterpreterOptionId = (form: WabunkaReservationForm | WabunkaReservationWithGuestTypeForm, isStep1?: boolean): string => {
  // NOTE: なぜこの判断が必要なのか？
  // Step1入力→予約画面に遷移して、再度Step1に戻ってきた場合、step2.experienceEventInterpreterOption.idが設定される。
  // それをそのまま使うとエラーになってしまう(新しく選択した体験イベントの情報と保持している情報にズレが生じる)ため、
  // Step1の場合は、体験イベントに紐づく通訳オプションの１つ目を指定する必要がある
  if (isStep1) {
    return form.experienceEvent.interpreterOptions[0]?.id;
  }

  // NOTE: なぜこの判断が必要なのか？
  // 体験詳細画面時点では通訳オプションが入力されることはない(その時のidは空文字となる)。
  // しかし料金計算エンドポイント実行のためには通訳オプションは必須項目なので、
  // 通訳オプションIDが空の場合は、体験イベントに紐づく通訳オプションの１つ目を指定する必要がある
  return form.step2.experienceEventInterpreterOption && form.step2.experienceEventInterpreterOption.id == ''
    ? form.experienceEvent.interpreterOptions[0]?.id
    : form.step2.experienceEventInterpreterOption.id;
};

export const buildExperiencePreReservationPriceCalculateRequest = (form: WabunkaReservationForm, isStep1?: boolean, currency?: string): WabunkaExperiencePreReservationPriceCalculateRequestInput => {
  return {
    experienceEventId: form.experienceEvent.id,
    guestsNumber: form.step1.numOfParticipants,
    experienceEventOptions: form.step2.experienceEventOptions.map((option) => ({
      experienceEventOptionId: option.experienceEventOption.id,
      count: option.count,
    })),
    experienceEventInterpreterOptionId: buildExperienceEventInterpreterOptionId(form, isStep1),
    codeName: form.step2.code.codeName,
    currency: currency,
  };
};

export const buildExperiencePreReservationPriceCalculateWithGuestTypeRequest = (
  form: WabunkaReservationWithGuestTypeForm,
  isStep1?: boolean,
  currency?: string,
): WabunkaExperiencePreReservationGuestTypeBasedPriceCalculationRequestInput => {
  return {
    calculationInput: {
      guestTypes: form.reservation.guestTypes,
      guestTypePeopleCountSets: [], // FIXME: ゲストタイプ毎ゲスト人数の実装はこれから
      experienceEventOptions: form.step2.experienceEventOptions.map((option) => ({
        experienceEventOptionId: option.experienceEventOption.id,
        count: option.count,
      })),
      experienceEventInterpreterOptionId: buildExperienceEventInterpreterOptionId(form, isStep1),
      experienceEventOptionReservedCountSets: [], // FIXME: experienceEventOptionReservedCountSetsの実装
    },
    codeName: form.step2.code.codeName,
    currency: currency,
  };
};

export interface ExperienceFeeRulePriceViewModel {
  experienceFeeRuleId: string;
  chargeType: WabunkaExperienceFeeChargeType;
  chargeableUnitCount: number;
  appliedPersonCount: number;
  unitPriceIncludingTax: number;
  totalPriceIncludingTax: number;
}

export interface ReservationGuestTypePriceSet {
  id: string;
  name: string;
  count: number;
  totalPriceIncludingTaxString: string;
  experienceFeeRulePrices: ExperienceFeeRulePriceViewModel[];
  totalPriceIncludingTax: number;
  currencyConvertedPrice?: CurrencyConvertedPriceViewModel;
}

export interface ReservationExperienceEventOptionPriceSet {
  id: string;
  name: string;
  count: number;
  unitPriceIncludingTaxString: string;
  totalPriceIncludingTaxString: string;
  currencyConvertedUnitPrice?: CurrencyConvertedPriceViewModel;
  currencyConvertedTotalPrice?: CurrencyConvertedPriceViewModel;
}

export interface ReservationExperienceEventInterpreterOptionPriceSet {
  id: string;
  name: string;
  count: number;
  unitPriceIncludingTax: number;
  unitPriceIncludingTaxString: string;
  currencyConvertedUnitPrice?: CurrencyConvertedPriceViewModel;
}

export interface ExperiencePreReservationPriceViewModel {
  experienceEventUnitPriceIncludingTaxString: string;
  guestsNumber: number;
  experienceEventOptionPriceSets: ReservationExperienceEventOptionPriceSet[];
  totalExperienceEventPriceIncludingTaxString: string;
  interpreterOptionPriceSets: ReservationExperienceEventInterpreterOptionPriceSet[];
  serviceFeeIncludingTaxString: string;
  discountAmountString: string;
  totalPriceIncludingTaxString: string;
  totalPriceIncludingTax: number;
  currencyConvertedPrice?: CurrencyConvertedPriceViewModel;
}

export interface ExperiencePreReservationPriceWithGuestTypeViewModel {
  guestTypePriceSets: ReservationGuestTypePriceSet[];
  /** ゲストタイプ別料金の合計（税込・数値）。Meta Conversion API の体験本体 item_price 等、計算用途向け。 */
  totalGuestTypePriceIncludingTax: number;
  experienceEventOptionPriceSets: ReservationExperienceEventOptionPriceSet[];
  interpreterOptionPriceSets: ReservationExperienceEventInterpreterOptionPriceSet[];
  discountAmountString: string;
  totalPriceIncludingTaxString: string;
  totalPriceIncludingTax: number;
  currencyConvertedPrice?: CurrencyConvertedPriceViewModel;
  currencyConvertedDiscountAmount?: CurrencyConvertedPriceViewModel;
  /** 割引前の税込合計（明細行の合計 = Subtotal (incl. tax)） */
  totalPriceIncludingTaxBeforeDiscount?: number;
  currencyConvertedTotalPriceIncludingTaxBeforeDiscount?: CurrencyConvertedPriceViewModel;
  /** 割引前の税抜小計（コード適用時のみ） */
  totalPriceExcludingTaxBeforeDiscountString?: string;
  currencyConvertedTotalPriceExcludingTaxBeforeDiscount?: CurrencyConvertedPriceViewModel;
  /** 割引後の税抜 = 課税対象額（税抜ベース割引のときのみ） */
  totalPriceExcludingTaxString?: string;
  currencyConvertedTotalPriceExcludingTax?: CurrencyConvertedPriceViewModel;
  /** 最終金額に含まれる消費税額（税抜ベース割引・コミッションのときのみ） */
  taxAmountString?: string;
  currencyConvertedTaxAmount?: CurrencyConvertedPriceViewModel;
}

export const buildExperiencePreReservationPriceViewModel = (res: WabunkaExperiencePreReservationPrice): ExperiencePreReservationPriceViewModel => {
  return {
    experienceEventUnitPriceIncludingTaxString: res.experienceEventUnitPriceIncludingTax.toLocaleString(),
    guestsNumber: res.guestsNumber,
    experienceEventOptionPriceSets: res.experienceEventOptionPriceSets.map((option) => ({
      id: option.id,
      name: option.name,
      count: option.count,
      unitPriceIncludingTaxString: option.unitPriceIncludingTax.toLocaleString(),
      totalPriceIncludingTaxString: option.totalPriceIncludingTax.toLocaleString(),
    })),
    totalExperienceEventPriceIncludingTaxString: res.totalExperienceEventPriceIncludingTax.toLocaleString(),
    interpreterOptionPriceSets: res.interpreterOptionPriceSets.map((option) => ({
      id: option.id,
      name: option.name,
      count: option.count,
      unitPriceIncludingTax: option.unitPriceIncludingTax,
      unitPriceIncludingTaxString: option.unitPriceIncludingTax.toLocaleString(),
    })),
    serviceFeeIncludingTaxString: res.serviceFeeIncludingTax.toLocaleString(),
    discountAmountString: res.discountAmount.toLocaleString(),
    totalPriceIncludingTaxString: res.totalPriceIncludingTax.toLocaleString(),
    totalPriceIncludingTax: res.totalPriceIncludingTax,
    currencyConvertedPrice: mapCurrencyConvertedPrice(res.currencyConvertedPrice),
  };
};

export const buildExperiencePreReservationPriceWithGuestTypeViewModel = (res: WabunkaExperienceGuestTypeBasedPrice): ExperiencePreReservationPriceWithGuestTypeViewModel => {
  return {
    guestTypePriceSets: res.guestTypePriceSets.map((guestType) => {
      return {
        id: guestType.guestTypeId,
        name: guestType.name,
        count: guestType.count,
        totalPriceIncludingTaxString: guestType.totalPriceIncludingTax.toLocaleString(),
        totalPriceIncludingTax: guestType.totalPriceIncludingTax,
        experienceFeeRulePrices: guestType.experienceFeeRulePrices || [],
        currencyConvertedPrice: mapCurrencyConvertedPrice(guestType.currencyConvertedPrice),
      };
    }),
    totalGuestTypePriceIncludingTax: res.totalGuestTypePriceIncludingTax,
    experienceEventOptionPriceSets: res.experienceEventOptionPriceSets.map((option) => ({
      id: option.id,
      name: option.name,
      count: option.count,
      unitPriceIncludingTaxString: option.unitPriceIncludingTax.toLocaleString(),
      totalPriceIncludingTaxString: option.totalPriceIncludingTax.toLocaleString(),
      currencyConvertedUnitPrice: mapCurrencyConvertedPrice(option.currencyConvertedUnitPrice),
      currencyConvertedTotalPrice: mapCurrencyConvertedPrice(option.currencyConvertedTotalPrice),
    })),
    interpreterOptionPriceSets: res.interpreterOptionPriceSets.map((option) => ({
      id: option.id,
      name: option.name,
      count: option.count,
      unitPriceIncludingTax: option.unitPriceIncludingTax,
      unitPriceIncludingTaxString: option.unitPriceIncludingTax.toLocaleString(),
      currencyConvertedUnitPrice: mapCurrencyConvertedPrice(option.currencyConvertedUnitPrice),
    })),
    discountAmountString: res.discountAmount.toLocaleString(),
    totalPriceIncludingTaxString: res.totalPriceIncludingTax.toLocaleString(),
    totalPriceIncludingTax: res.totalPriceIncludingTax,
    currencyConvertedPrice: mapCurrencyConvertedPrice(res.currencyConvertedPrice),
    currencyConvertedDiscountAmount: mapCurrencyConvertedPrice(res.currencyConvertedDiscountAmount),
    totalPriceIncludingTaxBeforeDiscount: res.totalPriceIncludingTaxBeforeDiscount,
    currencyConvertedTotalPriceIncludingTaxBeforeDiscount: mapCurrencyConvertedPrice(res.currencyConvertedTotalPriceIncludingTaxBeforeDiscount),
    totalPriceExcludingTaxBeforeDiscountString: res.totalPriceExcludingTaxBeforeDiscount?.toLocaleString(),
    currencyConvertedTotalPriceExcludingTaxBeforeDiscount: mapCurrencyConvertedPrice(res.currencyConvertedTotalPriceExcludingTaxBeforeDiscount),
    totalPriceExcludingTaxString: res.totalPriceExcludingTax?.toLocaleString(),
    currencyConvertedTotalPriceExcludingTax: mapCurrencyConvertedPrice(res.currencyConvertedTotalPriceExcludingTax),
    taxAmountString: res.taxAmount?.toLocaleString(),
    currencyConvertedTaxAmount: mapCurrencyConvertedPrice(res.currencyConvertedTaxAmount),
  };
};

export const createDefaultExperiencePreReservationPriceViewModel = (): ExperiencePreReservationPriceViewModel => {
  return {
    experienceEventUnitPriceIncludingTaxString: '',
    guestsNumber: 0,
    experienceEventOptionPriceSets: [],
    totalExperienceEventPriceIncludingTaxString: '',
    interpreterOptionPriceSets: [],
    serviceFeeIncludingTaxString: '',
    discountAmountString: '',
    totalPriceIncludingTaxString: '',
    totalPriceIncludingTax: 0,
  };
};

/** 総額の表示。円以外は換算額を出し、換算が届くまでは '...' を出す */
export const formatTotalPriceDisplay = (price: ExperiencePreReservationPriceWithGuestTypeViewModel | undefined, currency: string): string => {
  if (currency === 'JPY') return `¥${(price?.totalPriceIncludingTax ?? 0).toLocaleString()}`;
  return price?.currencyConvertedPrice?.formattedAmount ?? '...';
};

// 料金関連のヘルパー関数
export const parsePriceString = (priceString: string): number => {
  return Number.parseFloat(priceString.replace(/,/g, ''));
};

export const createPriceBreakdown = (feeRulePrices: ExperienceFeeRulePriceViewModel[], totalPrice: number): string => {
  if (feeRulePrices.length === 0) {
    return `¥${totalPrice.toLocaleString()}`;
  }

  return feeRulePrices
    .map((fee) => {
      const formattedPrice = `¥${fee.unitPriceIncludingTax.toLocaleString()}`;
      return fee.chargeableUnitCount === 1 ? formattedPrice : `${formattedPrice} × ${fee.chargeableUnitCount}`;
    })
    .join(' + ');
};

export const createDetailedPriceInfo = (set: ReservationGuestTypePriceSet) => {
  const totalPrice = set.totalPriceIncludingTax;
  const priceBreakdown = createPriceBreakdown(set.experienceFeeRulePrices, totalPrice);

  return {
    id: set.id,
    name: set.name,
    count: set.count,
    totalPrice,
    formattedUnitPrice: priceBreakdown,
    formattedTotalPrice: `¥${totalPrice.toLocaleString()}`,
  };
};
